import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { arrayHasItems } from '../../helpers/common';
import {
  DemandPlanningLoadingKeys,
  getComponentDemandLedgerTableData,
} from '../../slices/demandPlanningSlice';
import { LoaderSpinner } from '../LoaderSpinner';
import {
  buildComponentDemandSubCategoryGroups,
  filterComponentDemandLedgerRows,
  formatComponentDemandNumber,
  formatComponentDemandUnits,
  summarizeComponentDemandLedgerRows,
} from '../../lib/componentDemandLedgerHelpers';

const ComponentDemandLedgerTable = ({
  selectedForecastDemandPlanning,
  searchTerm,
  selectedCategoryId,
  selectedSubcategoryId,
  selectedStatusId,
}) => {
  const dispatch = useDispatch();
  const { componentDemandLedgerTableData, loadingKeys } = useSelector(state => state.demandPlanning);

  const [localSearch, setLocalSearch] = useState('');
  const [exceptionsOnly, setExceptionsOnly] = useState(false);
  const [replacementOnly, setReplacementOnly] = useState(false);

  const componentDemandLedgerTableLoading = loadingKeys.includes(
    DemandPlanningLoadingKeys.getComponentDemandLedgerTableData
  );

  const rows = useMemo(() => {
    const raw = componentDemandLedgerTableData?.rows;
    return arrayHasItems(raw) ? raw : [];
  }, [componentDemandLedgerTableData]);

  const filteredRows = useMemo(
    () =>
      filterComponentDemandLedgerRows(rows, {
        search: localSearch,
        exceptionsOnly,
        replacementOnly,
      }),
    [rows, localSearch, exceptionsOnly, replacementOnly]
  );

  const summary = useMemo(
    () => summarizeComponentDemandLedgerRows(filteredRows),
    [filteredRows]
  );

  const subCategoryGroups = useMemo(
    () => buildComponentDemandSubCategoryGroups(filteredRows).slice(0, 8),
    [filteredRows]
  );

  const forecastYear =
    componentDemandLedgerTableData?.forecastYear ??
    selectedForecastDemandPlanning?.forecast_year ??
    selectedForecastDemandPlanning?.forecastYear;

  useEffect(() => {
    const demandPlanningId = selectedForecastDemandPlanning?.id;
    const demandPlanningYear =
      selectedForecastDemandPlanning?.forecast_year ??
      selectedForecastDemandPlanning?.forecastYear;

    if (demandPlanningId == null || demandPlanningYear == null || demandPlanningYear === '') return;

    dispatch(
      getComponentDemandLedgerTableData({
        demandPlanningId,
        demandPlanningYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
      })
    );
  }, [
    dispatch,
    selectedForecastDemandPlanning?.id,
    selectedForecastDemandPlanning?.forecast_year,
    selectedForecastDemandPlanning?.forecastYear,
    searchTerm,
    selectedCategoryId,
    selectedSubcategoryId,
    selectedStatusId,
  ]);

  if (componentDemandLedgerTableLoading) {
    return (
      <section className="flex-1 px-4 py-4 lg:px-6">
        <div className="flex justify-center items-center h-full mt-8">
          <LoaderSpinner width="80" height="80" border="5" colorSpinner="gray" />
        </div>
      </section>
    );
  }

  return (
    <section className="mb-4 rounded-lg border border-gray-200 bg-white p-2 shadow-sm lg:p-4">
      <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <span className="mb-2 inline-flex rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-900">
            Component Demand Ledger
          </span>
          <h2 className="text-xl font-semibold tracking-[-0.02em] text-gray-900">
            Reconcile final supply demand by purchasable SKU before OTB consumption
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">
            One row per supply SKU: direct sales, bundle component demand from kit attribution, and
            replacement demand transferred from sunset SKUs. Final supply demand is the sum of those
            three layers.
            {forecastYear ? ` Units use forecast-year (${forecastYear}) snapshot sales.` : ''}
          </p>
        </div>
        <div className="flex flex-col gap-2 xl:flex-row xl:items-center">
          <input
            className="h-9 w-full min-w-[220px] rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 xl:w-[260px]"
            onChange={event => setLocalSearch(event.target.value)}
            placeholder="Search supply SKU or product"
            type="search"
            value={localSearch}
          />
          <label className="flex h-9 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-xs font-medium text-gray-900">
            <input
              checked={replacementOnly}
              className="size-3.5 rounded border-gray-300"
              onChange={event => setReplacementOnly(event.target.checked)}
              type="checkbox"
            />
            Replacement impacted only
          </label>
          <label className="flex h-9 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-xs font-medium text-gray-900">
            <input
              checked={exceptionsOnly}
              className="size-3.5 rounded border-gray-300"
              onChange={event => setExceptionsOnly(event.target.checked)}
              type="checkbox"
            />
            Exceptions only
          </label>
        </div>
      </div>

      <div className="mt-4 grid gap-2 md:grid-cols-2 xl:grid-cols-6">
        <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
          <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Supply SKUs</p>
          <p className="mt-1 text-lg font-semibold text-gray-900">
            {formatComponentDemandNumber(summary.supplySkuCount)}
          </p>
        </div>
        <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
          <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Direct demand</p>
          <p className="mt-1 text-lg font-semibold text-gray-900">
            {formatComponentDemandUnits(summary.totalDirectDemand)}
          </p>
        </div>
        <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
          <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Bundle demand</p>
          <p className="mt-1 text-lg font-semibold text-gray-900">
            {formatComponentDemandUnits(summary.totalBundleDemand)}
          </p>
        </div>
        <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
          <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Replacement demand</p>
          <p className="mt-1 text-lg font-semibold text-gray-900">
            {formatComponentDemandUnits(summary.totalReplacementDemand)}
          </p>
        </div>
        <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
          <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Final supply demand</p>
          <p className="mt-1 text-lg font-semibold text-gray-900">
            {formatComponentDemandUnits(summary.totalSupplyDemand)}
          </p>
        </div>
        <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
          <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Exceptions</p>
          <p className="mt-1 text-lg font-semibold text-gray-900">
            {formatComponentDemandNumber(summary.exceptionCount)}
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(260px,0.82fr)_minmax(0,1.7fr)]">
        <aside className="rounded-lg border border-gray-200 bg-white p-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Demand by sub-category</h3>
            <p className="mt-1 text-xs text-gray-500">
            This rail keeps final purchasing demand legible at the portfolio level while you inspect SKU-level rows.
            </p>
          </div>
          <div className="mt-4 space-y-2">
            {subCategoryGroups.length > 0 ? (
              subCategoryGroups.map(group => (
                <div key={group.subCategory} className="rounded-md border border-gray-200 bg-white px-3 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-gray-900">{group.subCategory}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {formatComponentDemandNumber(group.skuCount)} supply SKU
                        {group.skuCount === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">
                        {formatComponentDemandUnits(group.totalSupplyDemand)}
                      </p>
                      {group.replacementDemand > 0 ? (
                        <p className="mt-1 text-[11px] text-amber-700">
                          {formatComponentDemandUnits(group.replacementDemand)} replacement
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-md border border-dashed border-gray-300 bg-white px-4 py-6 text-sm text-gray-500">
                No ledger rows are in scope for the current filters.
              </div>
            )}
          </div>
        </aside>

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="max-h-[calc(100vh-18rem)] overflow-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50 text-xs uppercase tracking-[0.12em] text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Supply SKU</th>
                  <th className="px-4 py-3 font-medium">Product</th>
                  <th className="px-4 py-3 font-medium">Sub-category</th>
                  <th className="px-4 py-3 font-medium">Supplier</th>
                  <th className="px-4 py-3 font-medium text-right">Direct demand</th>
                  <th className="px-4 py-3 font-medium text-right">Bundle demand</th>
                  <th className="px-4 py-3 font-medium text-right">Replacement demand</th>
                  <th className="px-4 py-3 font-medium text-right">Final supply demand</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length > 0 ? (
                  filteredRows.map(row => (
                    <tr key={row.id} className="border-b border-gray-100 hover:bg-white">
                      <td className="px-4 py-3 align-top font-medium text-gray-900">{row.supplySku}</td>
                      <td className="px-4 py-3 align-top text-gray-600">{row.productName}</td>
                      <td className="px-4 py-3 align-top text-gray-600">{row.subCategory}</td>
                      <td className="px-4 py-3 align-top text-gray-600">{row.supplier}</td>
                      <td className="px-4 py-3 text-right align-top text-gray-900">
                        {formatComponentDemandUnits(row.directDemand)}
                      </td>
                      <td className="px-4 py-3 text-right align-top text-gray-900">
                        {formatComponentDemandUnits(row.bundleComponentDemand)}
                      </td>
                      <td className="px-4 py-3 text-right align-top text-gray-900">
                        {formatComponentDemandUnits(row.replacementDemand)}
                      </td>
                      <td className="px-4 py-3 text-right align-top font-medium text-gray-900">
                        {formatComponentDemandUnits(row.totalSupplyDemand)}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex flex-wrap gap-1.5">
                          <span
                            className={`inline-flex rounded-md px-2 py-1 text-[11px] font-medium ${
                              row.mismatch
                                ? 'bg-amber-50 text-amber-700'
                                : 'bg-emerald-50 text-emerald-700'
                            }`}
                          >
                            {row.mismatch ? 'Mismatch' : 'Reconciled'}
                          </span>
                          {row.noDemand ? (
                            <span className="inline-flex rounded-md bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-700">
                              Zero demand
                            </span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-4 py-10 text-center text-sm text-gray-500" colSpan={9}>
                      No component ledger rows match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComponentDemandLedgerTable;
